/**
 * Tiny shared API helper for blog + admin endpoints.
 * Uses REACT_APP_BACKEND_URL with /api prefix and always sends cookies for auth.
 *
 * IMPORTANT: We capture a reference to native fetch as early as possible so that
 * external tracker scripts (e.g. FollowUpBoss widget, Emergent monitoring) cannot
 * wrap window.fetch to consume the response body before our code reads it.
 */
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Snapshot native fetch on module load (before any third-party script wraps it).
// Note: window.__nativeFetch is set in index.html BEFORE any other script runs,
// guaranteeing we have an unwrapped reference even if FUB/Emergent monitoring
// wraps window.fetch afterward.
const nativeFetch =
  typeof window !== "undefined" && typeof window.__nativeFetch === "function"
    ? window.__nativeFetch
    : typeof window !== "undefined" && typeof window.fetch === "function"
      ? window.fetch.bind(window)
      : fetch;

async function request(path, { method = "GET", body, headers = {} } = {}) {
  const opts = {
    method,
    credentials: "include",
    headers: { ...headers },
  };
  if (body instanceof FormData) {
    opts.body = body;
  } else if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await nativeFetch(`${API_BASE}${path}`, opts);
  let data = null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    try {
      const t = await res.text();
      data = t || null;
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && (data.detail || data.error)) ||
      (typeof data === "string" ? data : null) ||
      `HTTP ${res.status}`;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Resolve a /api/blog/images/... URL to an absolute URL for the frontend
export const resolveImage = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Public
export const listArticles = (page = 1, category) =>
  request(`/blog/articles?page=${page}${category ? `&category=${encodeURIComponent(category)}` : ""}`);
export const getArticle = (slug) => request(`/blog/articles/${slug}`);
export const listCategories = () => request(`/blog/categories`);

// Auth
export const authMe = () => request(`/auth/me`);
export const authLogout = () => request(`/auth/logout`, { method: "POST" });
export const authLogin = (email, password) =>
  request(`/auth/login`, { method: "POST", body: { email, password } });

// Admin
export const adminListArticles = (statusFilter) =>
  request(`/admin/articles${statusFilter ? `?status_filter=${statusFilter}` : ""}`);
export const adminGetArticle = (id) => request(`/admin/articles/${id}`);
export const adminGenerateArticle = ({ topic, seed } = {}) =>
  request(`/admin/articles/generate`, {
    method: "POST",
    body: topic ? { topic } : seed ? { seed } : {},
  });
export const adminSuggestTopic = () =>
  request(`/admin/articles/suggest-topic`, { method: "POST" });
export const adminReviseArticle = (id, instruction) =>
  request(`/admin/articles/${id}/revise`, {
    method: "POST",
    body: { instruction },
  });
export const adminPatchArticle = (id, patch) =>
  request(`/admin/articles/${id}`, { method: "PATCH", body: patch });
export const adminRegenImage = (id) =>
  request(`/admin/articles/${id}/regenerate-image`, { method: "POST" });
export const adminUploadImage = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return request(`/admin/articles/${id}/upload-image`, { method: "POST", body: fd });
};
export const adminDeleteArticle = (id) =>
  request(`/admin/articles/${id}`, { method: "DELETE" });
